/**
 * displayId — canonical human-facing entity identifiers.
 *
 * RULE (2026-08-06): render the stored identifier VERBATIM. Never re-pad,
 * never fabricate. If no stored identifier exists, show the raw `id` or
 * '—' — a blank prompts a question; a fabrication doesn't.
 *
 * REFACTOR (§4, 2026-08-07): identifier alias resolution is now delegated to
 * `canonicalFields.js`. There is ONE alias system in the codebase, not two.
 * The accessors log divergence when two aliases disagree, which the old
 * inline `numberKeys` chain silently ignored.
 *
 * The migration delivered a coherent identifier scheme:
 *   Job         projectNumber "73"       (legacyId 34696)
 *   Job (new)   jobNumber "J-000002"     (net-new tenants)
 *   WO          number "34696-1"
 *   Quote       quoteNumber "Q-0030"
 *   Invoice     invoiceNumber "INV-0001"
 *
 * This module reads those fields via canonicalFields.js and returns them
 * unchanged.
 */

import {
  getQuoteNumber,
  getJobNumber,
  getInvoiceNumber,
  getWorkOrderNumber,
  getChangeOrderNumber,
} from './canonicalFields';

/**
 * Format an entity's display identifier.
 *
 * @param {object} entity   the record
 * @param {function} accessor  canonical accessor from canonicalFields.js
 */
function formatWith(entity, accessor) {
  if (!entity) return '—';

  const value = accessor(entity, entity.id);
  if (value != null && String(value).trim() !== '') {
    return String(value).trim();
  }

  // Fallback: raw id or dash. NEVER fabricate a prefixed number.
  const rawId = entity.id ?? entity._id ?? entity.pk ?? entity.PK;
  if (rawId) return String(rawId);
  return '—';
}

export const formatQuoteNumber = (e) => formatWith(e, getQuoteNumber);
export const formatJobNumber = (e) => formatWith(e, getJobNumber);
export const formatInvoiceNumberFor = (e) => formatWith(e, getInvoiceNumber);
export const formatWorkOrderNumber = (e) => formatWith(e, getWorkOrderNumber);

/**
 * IDI-411: a work order's HUMAN label, for anywhere a person reads it beside
 * other text (Quote Items, the Create Work Order line list, Job Costing).
 * Its number when it has one, else its title, else "Work order". NEVER the
 * internal id: formatWorkOrderNumber falls back to the raw id, and app-created
 * work orders have no number, so every covered line on J-000030 read
 * "Kraft Batts R-19wo-1792027751693-yhsw0d".
 */
export function workOrderLabel(wo) {
  if (!wo) return '';
  const num = getWorkOrderNumber(wo, wo.id);
  if (num != null && String(num).trim() !== '' && !isInternalWoId(num, wo)) return String(num).trim();
  const title = String(wo.title || wo.name || '').trim();
  if (title && !isInternalWoId(title, wo)) return title;
  return 'Work order';
}

// An internal WO id (wo-<epoch>-<rand>, or the record's own id).
function isInternalWoId(v, wo) {
  const s = String(v).trim();
  return s === String(wo?.id ?? '') || /^wo-\d{10,}-[a-z0-9]+$/i.test(s);
}
export const formatChangeOrderNumber = (e) => formatWith(e, getChangeOrderNumber);

/**
 * Numeric-safe sort key for display identifiers.
 *
 * §1d (2026-08-07): identifiers come in two shapes on the same list:
 *   migrated:  bare integers        "73"       "100"  "9"
 *   net-new:   zero-padded prefixed "J-000002" "J-000100"
 *
 * Lexicographic sort produces "100" < "73" < "9" for the bare integers.
 * On a tenant with mixed data, sorting by job number clusters migrated jobs
 * in arbitrary order at one end while net-new jobs sort correctly.
 *
 * This function returns a comparable key that sorts numerically:
 *   - Extracts the trailing numeric run (handles both "73" and "J-000002")
 *   - Pads to 12 digits so numeric comparison works lexicographically
 *   - Preserves any non-numeric prefix as a secondary sort key
 *
 * Usage:
 *   arr.sort((a, b) => sortKey(formatJobNumber(a)).localeCompare(sortKey(formatJobNumber(b))))
 *   or  arr.sort((a, b) => compareIds(formatJobNumber(a), formatJobNumber(b)))
 *
 * @param {string} id  A formatted display identifier
 * @returns {string}   A padded, lexicographically-comparable key
 */
export function sortKey(id) {
  if (id == null || id === '' || id === '—') return 'zzzzzzzzzzzz'; // sort dashes last
  const s = String(id);
  // Extract non-numeric prefix + trailing numeric run.
  // Matches: "J-000002" → prefix="J-", num="000002"
  //          "73"       → prefix="",   num="73"
  //          "34696-1"  → prefix="34696-", num="1"  (WO shape)
  //          "abc"      → prefix="abc", num=""
  const m = s.match(/^(.*?)(\d+)$/);
  if (!m) return s.toLowerCase().padEnd(12, ' ');
  const prefix = m[1].toLowerCase();
  const num = m[2].padStart(12, '0');
  return prefix + num;
}

/**
 * Compare two display identifiers numerically-aware.
 * Returns negative / zero / positive suitable for Array.sort.
 */
export function compareIds(a, b) {
  const ka = sortKey(a);
  const kb = sortKey(b);
  return ka < kb ? -1 : ka > kb ? 1 : 0;
}

/**
 * Format a bare numeric sequence (for previews where no record exists yet).
 * Kept for backward compatibility with call sites that only have a sequence
 * number, not a record.
 */
export function formatSequence(prefix, sequence) {
  if (sequence == null) return `${prefix}-`;
  return `${prefix}-${sequence}`;
}

/**
 * Legacy displayId function — kept for backward compatibility.
 * Prefer the specific formatters above.
 *
 * @deprecated Use formatQuoteNumber / formatJobNumber / etc. directly.
 */
export function displayId(entity, prefix, numberKeys = []) {
  if (!entity) return '—';
  for (const k of numberKeys) {
    const v = entity[k];
    if (v != null && String(v).trim() !== '') return String(v).trim();
  }
  const rawId = entity.id ?? entity._id ?? entity.pk ?? entity.PK;
  return rawId ? String(rawId) : '—';
}
